import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const plans = [
  {
    name: 'Free',
    price: '₹0',
    period: '/mo',
    members: '3 members',
    sops: '5 SOPs',
    highlight: false,
    bgColor: 'bg-blue',
    textColor: 'text-white',
    borderColor: 'border-blue-dark',
  },
  {
    name: 'Launch',
    price: '₹4,999',
    period: '/mo',
    members: '10 members',
    sops: '25 SOPs',
    highlight: true,
    bgColor: 'bg-yellow',
    textColor: 'text-accent-foreground',
    borderColor: 'border-yellow-dark',
  },
  {
    name: 'Growth',
    price: '₹12,499',
    period: '/mo',
    members: '25 members',
    sops: 'Unlimited',
    highlight: false,
    bgColor: 'bg-red',
    textColor: 'text-white',
    borderColor: 'border-red-dark',
  },
];

export function PricingInline() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <div ref={ref}>
      <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1, duration: 0.4 }}
            className={cn(
              "relative rounded-2xl border p-5 text-center shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all",
              plan.bgColor + "/80",
              "backdrop-blur-xl",
              plan.borderColor + "/50"
            )}
          >
            {plan.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-background/80 backdrop-blur-sm text-foreground text-xs font-bold rounded-full border border-border/50 shadow-md">
                Popular
              </div>
            )}

            <h3 className={cn("font-semibold text-lg mb-2", plan.textColor)}>
              {plan.name}
            </h3>
            
            <div className="mb-4">
              <span className={cn("text-3xl font-bold", plan.textColor)}>{plan.price}</span>
              <span className={cn("text-sm opacity-80", plan.textColor)}>{plan.period}</span>
            </div>

            <div className={cn("space-y-2 text-sm mb-4", plan.textColor, "opacity-90")}>
              <div className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />
                {plan.members}
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />
                {plan.sops}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 0.4 }}
        className="text-center mt-8"
      >
        <Link to="/auth">
          <Button size="lg" variant="hero">
            Start Free
          </Button>
        </Link>
        <p className="text-xs text-muted-foreground mt-3">
          Upgrade anytime. Cancel anytime. We're not trying to trap you.
        </p>
      </motion.div>
    </div>
  );
}
